import { type ChangeEvent, useState } from "react";
import { Col, Form, Row, Spinner } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import type { PlatformProfile } from "shared";
import { z } from "zod/v4";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { useProfile } from "../auth/useProfile";
import { useForm } from "../common/form/useForm";
import { Icon } from "../common/Icon";
import { ExtraForm } from "../common/pure/ExtraFormGroup";
import { useCompany } from "../company/useCompany";
import { Button } from "../components/Button";
import { Select } from "../components/Select";
import { Tabs } from "../components/Tabs";
import { LinkGen } from "../LinkGen";
import { AppPage } from "../layout/AppLayout";
import { TabApps } from "./TabApps";
import { TabBilling } from "./TabBilling";
import { TabCompanies } from "./TabCompanies";
import { TabCompanyInfo } from "./TabCompanyInfo";
import { TabDemoSettings } from "./TabDemoSettings";
import { TabGuidelines } from "./TabGuidelines";
import { TabIdealCustomerEdit } from "./TabIdealCustomerEdit";
import { TabMarketingPlan } from "./TabMarketingPlan";
import { TabNotifications } from "./TabNotifications";
import { TabTeamAccess } from "./TabTeamAccess";

const userSchema = z.object({
	firstName: z
		.string({
			error: "First Name field is required.",
		})
		.min(2, "Must be at least 2 characters long."),
	lastName: z
		.string({
			error: "Last Name field is required.",
		})
		.min(2, "Must be at least 2 characters long."),
	subscribed: z.boolean({}),
});

const passwordSchema = z
	.object({
		currentPassword: z.string({
			error: "Password field is required.",
		}),
		newPassword: z
			.string({
				error: "New Password field is required.",
			})
			.min(8, "Must be at least 8 characters long."),
		repeatPassword: z.string({
			error: "Confirm Password field is required.",
		}),
	})
	.refine(
		(values) => {
			return values.newPassword === values.repeatPassword;
		},
		{
			message: "Passwords must match!",
			path: ["repeatPassword"],
		},
	);

export const ShowChangePlatformProfileName = (props: {
	profile?: PlatformProfile | null;
	onEdit?: () => Promise<PlatformProfile[]>;
	onSelect?: (page: PlatformProfile) => Promise<void>;
	emptyLabel?: string;
}) => {
	const [isEditing, setIsEditing] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [pages, setPages] = useState<PlatformProfile[]>([]);

	const handleEditClick = async () => {
		setIsLoading(true);
		if (props.onEdit) {
			try {
				setPages(await props.onEdit());
			} catch {
				setIsLoading(false);
				setIsEditing(false);
				return;
			}
		}
		setIsLoading(false);
		setIsEditing(true);
	};

	const handlePageSelect = async (event: ChangeEvent<HTMLSelectElement>) => {
		setIsLoading(true);
		if (props.onSelect) {
			const profile = pages.find((page) => page.id === event.target.value);
			if (profile && profile.id !== "-1") {
				await props.onSelect(profile);
			}
		}
		setIsLoading(false);
		setIsEditing(false);
	};

	if (!props.profile && !props.onEdit) return null;

	return (
		<div className="my-3 d-flex flex-row gap-2 align-items-center">
			{!isEditing ? (
				<>
					{props.profile?.imageUrl && (
						<img
							src={props.profile.imageUrl}
							alt=""
							width={32}
							height={32}
							className="rounded-circle"
						/>
					)}
					<div>
						{props.profile ? (
							<strong>{props.profile.name}</strong>
						) : (
							<span className="text-muted">
								{props.emptyLabel ?? "Not selected"}
							</span>
						)}
					</div>
					<div>
						{props.onEdit &&
							(isLoading ? (
								<Spinner size="sm" />
							) : (
								<Icon
									style={{ cursor: "pointer" }}
									name="edit"
									onClick={handleEditClick}
								/>
							))}
					</div>
				</>
			) : null}
			{isEditing ? (
				!isLoading ? (
					<Select value={props.profile?.id ?? ""} onChange={handlePageSelect}>
						{!props.profile && (
							<option value="" disabled>
								Select...
							</option>
						)}
						{pages.map((page) => (
							<option key={page.id} value={page.id}>
								{page.name}
							</option>
						))}
					</Select>
				) : (
					<Spinner size="sm" />
				)
			) : null}
		</div>
	);
};

export const PageSettings = () => {
	const { tab = "apps" } = useParams();
	const { updateUserInfo, updateUserPassword, profile } = useProfile();
	const company = useCompany();
	const [showPasswordForm, setShowPasswordForm] = useState(false);
	const [showPasswords, setShowPasswords] = useState(false);
	const navigate = useNavigate();

	const userForm = useForm({
		validationSchema: toFormikValidationSchema(userSchema),
		initialValues: {
			firstName: profile.firstName,
			lastName: profile.lastName,
			subscribed: profile.subscribed,
		},
		onSubmit: (values) => updateUserInfo.mutateAsync(values),
	});
	const passwordForm = useForm({
		validationSchema: toFormikValidationSchema(passwordSchema),
		initialValues: {
			currentPassword: "",
			newPassword: "",
			repeatPassword: "",
		},
		onSubmit: (values) => updateUserPassword.mutateAsync(values),
	});

	return (
		<AppPage
			title="Settings"
			subtitle="Manage integrations, billing, and account preferences."
			className="settings"
		>
			<Tabs
				// change key to handle url changes within the page
				// this way if the tab param changes on the page it will update the active tab
				key={`settings-tab-${tab}`}
				defaultActiveKey={tab}
				onTabChange={(tab) => {
					navigate(LinkGen.Settings.Tab(tab || "apps"));
				}}
			>
				<Tabs.Panel eventKey="apps" title="Applications">
					<TabApps />
				</Tabs.Panel>

				<Tabs.Panel eventKey="team-access" title="Team & clients">
					<TabTeamAccess />
				</Tabs.Panel>

				<Tabs.Panel eventKey="billing" title="Billing">
					<TabBilling />
				</Tabs.Panel>

				<Tabs.Panel eventKey="notifications" title="Notifications">
					<TabNotifications />
				</Tabs.Panel>

				<Tabs.Panel eventKey="user" title="User Settings">
					<Row>
						<Col md={4}>
							<h2 className="my-3">User Settings</h2>

							<Form onSubmit={userForm.handleSubmit}>
								<div className="full-name">
									<ExtraForm.Group className="text-start" label="First Name">
										<ExtraForm.Control
											placeholder="John"
											type="text"
											data-testid="card-auth-first-name"
											{...userForm.getFieldProps("firstName")}
											{...userForm.getFieldState("firstName")}
										/>
									</ExtraForm.Group>

									<ExtraForm.Group className="text-start" label="Last Name">
										<ExtraForm.Control
											placeholder="Doe"
											type="text"
											data-testid="card-auth-last-name"
											{...userForm.getFieldProps("lastName")}
											{...userForm.getFieldState("lastName")}
										/>
									</ExtraForm.Group>
								</div>

								<Form.Group className="mb-3">
									<Form.Check
										type="switch"
										label="Subscribe to our weekly newsletter"
										id="settings-subscribe-switch"
										{...userForm.getFieldProps("subscribed")}
										{...userForm.getFieldState("subscribed")}
										checked={userForm.values.subscribed}
									/>
								</Form.Group>

								<Button
									full
									className="my-3"
									loading={userForm.isSubmitting}
									type="submit"
								>
									Save
								</Button>
							</Form>
						</Col>
					</Row>
					<Row>
						<Col md={4}>
							<h2 className="my-3">Password</h2>

							{showPasswordForm ? (
								<Form onSubmit={passwordForm.handleSubmit}>
									<ExtraForm.Group
										className="text-start"
										label="Current Password"
									>
										<ExtraForm.Control
											placeholder="Current Password"
											type={showPasswords ? "text" : "password"}
											data-testid="card-auth-current-password"
											{...passwordForm.getFieldProps("currentPassword")}
											{...passwordForm.getFieldState("currentPassword")}
										/>
									</ExtraForm.Group>

									<ExtraForm.Group className="text-start" label="New password">
										<ExtraForm.Control
											placeholder="New Password"
											type={showPasswords ? "text" : "password"}
											data-testid="card-auth-new-password"
											{...passwordForm.getFieldProps("newPassword")}
											{...passwordForm.getFieldState("newPassword")}
										/>
									</ExtraForm.Group>

									<ExtraForm.Group
										className="text-start"
										label="Repeat password"
									>
										<ExtraForm.Control
											placeholder="Repeat Password"
											type={showPasswords ? "text" : "password"}
											data-testid="card-auth-repeat-password"
											{...passwordForm.getFieldProps("repeatPassword")}
											{...passwordForm.getFieldState("repeatPassword")}
										/>
									</ExtraForm.Group>
									<Form.Check
										type="checkbox"
										label="Show passwords"
										checked={showPasswords}
										onChange={() => setShowPasswords((v) => !v)}
										className="mb-3"
									/>
									<Button
										full
										type="submit"
										loading={passwordForm.isSubmitting}
									>
										Change Password
									</Button>
								</Form>
							) : (
								<Button
									full
									type="button"
									onClick={() => setShowPasswordForm(true)}
								>
									Change Password
								</Button>
							)}
						</Col>
					</Row>
				</Tabs.Panel>
				<Tabs.Panel eventKey="marketing-plan" title="Marketing Plan">
					<TabMarketingPlan />
				</Tabs.Panel>
				<Tabs.Panel eventKey="post-guidelines" title="Post Guidelines">
					<TabGuidelines />
				</Tabs.Panel>
				<Tabs.Panel
					eventKey="ideal-customer-profiles"
					title="Ideal Customer Profiles"
				>
					<TabIdealCustomerEdit />
				</Tabs.Panel>
				<Tabs.Panel eventKey="company-info" title="Company Info">
					<TabCompanyInfo />
				</Tabs.Panel>
				<Tabs.Panel eventKey="companies" title="Companies">
					<TabCompanies />
				</Tabs.Panel>
				{company.demo ? (
					<Tabs.Panel eventKey="demo" title="Demo Settings">
						<TabDemoSettings />
					</Tabs.Panel>
				) : null}
			</Tabs>
		</AppPage>
	);
};
